import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronDown, Shield, Target, Rocket, Unlock } from "lucide-react";
import { BRAND } from "@/lib/brand";
import heroBackground from "@/assets/hero-background.jpg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AuthLink } from "@/components/navigation/AuthLink";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { ScrollToTop } from "@/components/ui/scroll-to-top";

// ---------- BEGIN COPY ----------
const copy = {
  en: {
    brand: "AcquizGroup",
    nav: {
      sellers: "Sellers",
      buyers: "Buyers",
      brokers: "Broker‑Sellers",
      consultants: "Consultants",
      partners: "Business Partners",
      lenders: "Lenders",
      login: "Log in",
      getStarted: "Get started",
    },
    hero: {
      eyebrow: "Deal Flow • Financing • Closing",
      title: "Innovation at the Service of Business Transfer",
      subtitle:
        "One platform that aligns sellers, buyers, brokers, consultants, partners, and lenders with standardized workflows and pre‑financing signals.",
      ctaPrimary: "Create your account",
      ctaSecondary: "See how it works",
    },
    mission: {
      title: "A faster, clearer path from Listing to Closing",
      text: "At AcquizGroup, we are transforming the business transfer landscape by eliminating traditional barriers. Our mission is clear: accelerate access to capital, neutralize allocation biases, and optimize closing times. We don't just change processes; we reverse the odds of failure to maximize the success of high-potential deals.",
    },
    whyChoose: {
      title: "Why AcquizGroup is your strategic choice",
      subtitle: "We bring you performance, objectivity, and complete freedom.",
      cards: [
        {
          icon: "Shield",
          title: "Absolute confidentiality",
          text: "Your information is protected at every step.",
        },
        {
          icon: "Target",
          title: "Objectivity as a Standard",
          text: "We believe that the viability of a project rests on the strength of its fundamentals. We highlight key indicators—such as profit margin and a DSCR > 1.3—to guarantee the quality of each file.",
        },
        {
          icon: "Rocket",
          title: "Performance Without Prospecting",
          text: "More than just a matchmaking service, we are your engine for opportunities. We bring you highly qualified client leads directly into your pipeline, without any prospecting effort on your part.",
        },
        {
          icon: "Unlock",
          title: "Freedom and Autonomy",
          text: "Our role is to streamline the ecosystem. We qualify opportunities, but you retain complete control: we never interfere with your final credit decisions.",
        },
      ],
    },
    how: {
      title: "How it works",
      sellerLabel: "Sellers",
      sellerSteps: [
        { k: "profile", t: "Create Profile" },
        { k: "submit", t: "Pre-Evaluation" },
        { k: "evaluation", t: "Evaluation" },
        { k: "dealmonitor", t: "Deal Monitor" },
        { k: "prefinancing", t: "Pre-Financing" },
        { k: "nda", t: "NDA" },
        { k: "contact", t: "Buyer Contact" },
        { k: "loi", t: "LOI" },
        { k: "closing", t: "Closing" },
      ],
      buyerLabel: "Buyers",
      buyerSteps: [
        { k: "profile", t: "Create Profile" },
        { k: "plan", t: "Choose Plan" },
        { k: "nda", t: "Sign NDAs" },
        { k: "contact", t: "Contact Consultants/Sellers" },
        { k: "loi", t: "LOI" },
        { k: "financing", t: "Choose Pre-Financing Offer" },
        { k: "closing", t: "Closing" },
      ],
      brokerLabel: "Brokers",
      brokerSteps: [
        { k: "profile", t: "Create Profile" },
        { k: "listing", t: "Create Listing" },
        { k: "evaluation", t: "In-House Evaluation" },
        { k: "dealmonitor", t: "Deal Monitor" },
        { k: "prefinancing", t: "Pre-Financing" },
        { k: "nda", t: "NDA" },
        { k: "contact", t: "Buyer Contact" },
        { k: "loi", t: "LOI" },
        { k: "closing", t: "Closing" },
      ],
      consultantLabel: "Consultants",
      consultantSteps: [
        { k: "profile", t: "Create Profile" },
        { k: "background", t: "Background Check" },
        { k: "directory", t: "Added to Directory" },
        { k: "contact", t: "Platform Users Contact" },
        { k: "services", t: "Sell Services or Knowledge (Fee or Equity)" },
      ],
      blurb:
        "Standardized steps, vendor‑side preparation, and lender‑friendly packaging reduce friction and accelerate decisions.",
    },
    grid: {
      title: "Built for every role in the transaction",
    },
    personas: {
      sellers: {
        title: "Sellers",
        points: [
          "Vendor Due Diligence options to reduce surprises",
          "Buyer pipeline with pre‑financing signals",
          "Transparent progress across NDA → LOI → Financing → Closing",
          "Fixed fees per submitted company",
          "Platform management: only 1%",
        ],
        cta: "List your business",
      },
      buyers: {
        title: "Buyers",
        points: [
          "Structured dossiers and ready‑to‑review financials",
          "Connect with lenders open to non‑conventional structures",
          "Track diligence and offers in one place",
          "Connection with experts and partners at your fingertips",
        ],
        cta: "Find opportunities",
      },
      brokers: {
        title: "Broker‑Sellers",
        badge: "New",
        points: [
          "Accelerate active mandates with pre‑financing workflows",
          "Reactivate dormant files and recover value",
          "Fixed fees per submitted company",
          "Platform management: only 1%",
        ],
        cta: "Submit mandates",
      },
      consultants: {
        title: "Consultants",
        points: [
          "Grow your professional client base",
          "Participate in acquisitions",
          "Create business partnerships",
          "Be masters of your negotiations",
        ],
        cta: "Join directory",
      },
      partners: {
        title: "Business Partners",
        points: [
          "Access pre‑qualified, vendor‑prepared opportunities",
          "Continuous stream matched to your criteria",
          "High visibility among platform users",
        ],
        cta: "Become a partner",
      },
      lenders: {
        title: "Lenders",
        points: [
          "Non‑conventional friendly pipeline: mezzanine, seller‑note, ABL, private debt, LBO, M&A lenders",
          "Focus on enterprise value and cash flows",
          "Faster packaging and decision support",
          "Offers visible on listings, identity confidential until LOI",
        ],
        cta: "Review pipeline",
      },
    },
    credibility: {
      items: [
        {
          h: "Aligned incentives",
          p: "AcquizGroup connects you so we can all row in the same direction. SUCCESS!",
        },
        {
          h: "Preparation first",
          p: "Vendor‑side preparation and lender‑ready files reduce rework and timeline risk.",
        },
        {
          h: "Visibility",
          p: "Dashboards and standardized artifacts create clarity across all parties.",
        },
      ],
    },
    faq: {
      title: "Frequently asked questions",
      categories: [
        {
          category: "General",
          target: "All users",
          items: [
            {
              q: "What is AcquizGroup?",
              a: "AcquizGroup is a deal-flow platform that connects sellers, buyers, brokers, consultants, partners, and lenders in one ecosystem to accelerate business transfers.",
            },
            {
              q: "How is AcquizGroup different from other platforms?",
              a: "We provide end-to-end standardization: vendor-side preparation, pre-financing signals, and unified workflows that reduce timeline risk and improve success rates.",
            },
            {
              q: "Who can use the platform?",
              a: "Business sellers, buyers, brokers, consultants, business partners, and lenders. Each role has tailored features and workflows.",
            },
          ],
        },
        {
          category: "Consultants Program (Operational Role)",
          target: "Buyers and Sellers who need to strengthen their project.",
          items: [
            {
              q: "What is the difference between a Consultant and an Internal Expert?",
              a: "The Consultant has an operational and strategic role. They can become your business partner, provide a specific license, or manage the business with you. The Internal Expert focuses solely on the mechanics of the transaction (legal, tax, notary).",
            },
            {
              q: "Can I integrate a consultant as a business partner (Equity)?",
              a: "Absolutely. AcquizGroup facilitates connecting you with experts ready to exchange their skills or licenses for an equity stake, which strengthens your case with banks.",
            },
            {
              q: "How are consultants vetted?",
              a: "Each consultant undergoes a background check and verification of their skills/licenses before being listed in our directory.",
            },
          ],
        },
        {
          category: "Our Experts Program (Transactional Role)",
          target: "Users in the Due Diligence and Closing phases.",
          items: [
            {
              q: "Who are AcquizGroup's internal experts?",
              a: "They are legal and financial professionals (Business Lawyers, Immigration Lawyers, Notaries, CPAs, Tax Advisors) specializing exclusively in the business transfer process.",
            },
            {
              q: "Am I required to use the platform's experts?",
              a: "No. You are free to use your own advisors. However, our experts are already trained in the platform's workflows, which significantly accelerates closing times and reduces coordination costs.",
            },
            {
              q: "How do the experts get involved in my case?",
              a: "They are accessible directly through your dashboard. They are involved in contract review, tax structuring, financial audit (Due Diligence), and the final signing.",
            },
          ],
        },
        {
          category: "Financing & Lenders",
          target: "Buyers and Lenders.",
          items: [
            {
              q: "What is \"Pre-financing\" on a listing?",
              a: "This means that the seller's file has been analyzed based on fundamental metrics (DSCR, cash flow) and that they have already received expressions of interest or financing proposals from our partner lenders.",
            },
            {
              q: "Do you accept unconventional financing?",
              a: "Yes. Our network includes lenders specializing in mezzanine debt, seller financing, ABL (asset-based lending), and private equity, who look at the company's potential beyond just personal credit.",
            },
          ],
        },
        {
          category: "Confidentiality & Security",
          target: "General",
          items: [
            {
              q: "Who can see my confidential information?",
              a: "No one, until a Non-Disclosure Agreement (NDA) has been digitally signed on the platform. Even after that, you control who has access to your \"Data Room.\"",
            },
            {
              q: "How does AcquizGroup replace my current tools?",
              a: "Everything is integrated: document signing (NDA/LOI), secure storage, video conferencing, and progress tracking. You no longer need to juggle between Zoom, Dropbox, and your email chains.",
            },
          ],
        },
      ],
    },
    cta: {
      title: "Ready to move faster?",
      subtitle: "Explore our plans tailored to your role.",
      btnSellers: "Seller Plans",
      btnBuyers: "Buyer Plans",
      btnConsultants: "Consultant Plans",
    },
    partnersCta: {
      title: "Ready to be a part of the Ecosystem?",
      btnPrimary: "Contact the team",
      btnSecondary: "Join the Ecosystem",
    },
    lendersCta: {
      title: "Ready to finance the future?",
      btnPrimary: "Contact the team",
      btnSecondary: "Finance the future",
    },
    footer: {
      legal: "© ",
    },
  },
  fr: {
    brand: "AcquizGroup",
    nav: {
      sellers: "Vendeurs",
      buyers: "Acheteurs",
      brokers: "Courtiers‑Vendeurs",
      consultants: "Consultants",
      partners: "Partenaires d'affaires",
      lenders: "Prêteurs",
      login: "Connexion",
      getStarted: "Commencer",
    },
    hero: {
      eyebrow: "Deal Flow • Financement • Closing",
      title: "L'innovation au service de la transmission d'entreprise",
      subtitle:
        "Une plateforme qui aligne vendeurs, acheteurs, courtiers, consultants, partenaires et prêteurs avec des workflows standardisés et des signaux de pré‑financement.",
      ctaPrimary: "Créer mon compte",
      ctaSecondary: "Voir le fonctionnement",
    },
    mission: {
      title: "Un chemin plus rapide et plus clair, de l'annonce au closing",
      text: "Chez AcquizGroup, nous transformons le paysage de la transmission d'entreprise en éliminant les barrières traditionnelles. Notre mission est claire : accélérer l'accès au capital, neutraliser les biais d'allocation et optimiser les délais de closing. Nous ne changeons pas simplement les processus ; nous renversons les probabilités d'échec pour maximiser le succès des transactions à fort potentiel.",
    },
    whyChoose: {
      title: "Pourquoi AcquizGroup est votre choix stratégique",
      subtitle: "Nous vous apportons performance, objectivité et liberté totale.",
      cards: [
        {
          icon: "Shield",
          title: "Confidentialité absolue",
          text: "Vos informations sont protégées à chaque étape.",
        },
        {
          icon: "Target",
          title: "L'Objectivité comme Standard",
          text: "Nous croyons que la viabilité d'un projet repose sur la solidité de ses fondamentaux. Nous mettons en avant des indicateurs clés — tels que la marge bénéficiaire et un DSCR > 1,3 — pour garantir la qualité de chaque dossier.",
        },
        {
          icon: "Rocket",
          title: "Performance sans Prospection",
          text: "Plus qu'une simple mise en relation, nous sommes votre moteur d'opportunités. Nous vous apportons des flux de clients hautement qualifiés directement dans votre pipeline, sans aucun effort de prospection de votre part.",
        },
        {
          icon: "Unlock",
          title: "Liberté et Autonomie",
          text: "Notre rôle est de fluidifier l'écosystème. Nous qualifions les opportunités, mais vous gardez le contrôle total : nous n'interférons jamais dans vos décisions finales de crédit.",
        },
      ],
    },
    how: {
      title: "Comment ça marche",
      sellerLabel: "Vendeurs",
      sellerSteps: [
        { k: "profile", t: "Créer profil" },
        { k: "submit", t: "Pré-évaluation" },
        { k: "evaluation", t: "Évaluation" },
        { k: "dealmonitor", t: "Deal Monitor" },
        { k: "prefinancing", t: "Préfinancement" },
        { k: "nda", t: "NDA" },
        { k: "contact", t: "Contact acheteurs" },
        { k: "loi", t: "LOI" },
        { k: "closing", t: "Closing" },
      ],
      buyerLabel: "Acheteurs",
      buyerSteps: [
        { k: "profile", t: "Créer profil" },
        { k: "plan", t: "Choisir forfait" },
        { k: "nda", t: "Signer les NDA" },
        { k: "contact", t: "Contact consultants/vendeurs" },
        { k: "loi", t: "LOI" },
        { k: "financing", t: "Choisir offre préfinancement" },
        { k: "closing", t: "Closing" },
      ],
      brokerLabel: "Courtiers",
      brokerSteps: [
        { k: "profile", t: "Créer profil" },
        { k: "listing", t: "Créer listing" },
        { k: "evaluation", t: "Évaluation maison" },
        { k: "dealmonitor", t: "Deal Monitor" },
        { k: "prefinancing", t: "Préfinancement" },
        { k: "nda", t: "NDA" },
        { k: "contact", t: "Contact acheteurs" },
        { k: "loi", t: "LOI" },
        { k: "closing", t: "Closing" },
      ],
      consultantLabel: "Consultants",
      consultantSteps: [
        { k: "profile", t: "Créer profil" },
        { k: "background", t: "Background check" },
        { k: "directory", t: "Ajout au répertoire" },
        { k: "contact", t: "Contact utilisateurs" },
        { k: "services", t: "Vendre services ou connaissances (rémunération ou équité)" },
      ],
      blurb:
        "Des étapes standardisées, une préparation côté vendeur et un packaging adapté aux prêteurs réduisent la friction et accélèrent les décisions.",
    },
    grid: {
      title: "Conçue pour chaque rôle de la transaction",
    },
    personas: {
      sellers: {
        title: "Vendeurs",
        points: [
          "Options de Vendor Due Diligence pour réduire les surprises",
          "Canal d'acheteurs avec signaux de pré‑financement",
          "Suivi clair NDA → LOI → Financement → Closing",
          "Frais fixes par entreprise soumise",
          "Gestion de plateforme : seulement 1%",
        ],
        cta: "Publier mon entreprise",
      },
      buyers: {
        title: "Acheteurs",
        points: [
          "Dossiers structurés et états financiers prêts à l'analyse",
          "Connexion avec des prêteurs ouverts aux structures non conventionnelles",
          "Suivi centralisé des diligences et des offres",
          "Connexion avec des experts et partenaires au bout des doigts",
        ],
        cta: "Trouver des opportunités",
      },
      brokers: {
        title: "Courtiers‑Vendeurs",
        badge: "Nouveau",
        points: [
          "Accélérez vos mandats actifs avec des workflows de pré‑financement",
          "Réactivez vos dossiers inactifs et récupérez de la valeur",
          "Frais fixes par entreprise soumise",
          "Gestion de plateforme : seulement 1%",
        ],
        cta: "Soumettre des mandats",
      },
      consultants: {
        title: "Consultants",
        points: [
          "Augmentez votre banque de clients professionnels",
          "Participez à des acquisitions",
          "Créez des partenariats d'affaires",
          "Soyez maîtres de vos négociations",
        ],
        cta: "Rejoindre l'annuaire",
      },
      partners: {
        title: "Partenaires d'affaires",
        points: [
          "Accès à des opportunités pré‑qualifiées et préparées côté vendeur",
          "Flux continu assorti à vos critères",
          "Grande visibilité auprès des utilisateurs de la plateforme",
        ],
        cta: "Devenir partenaire",
      },
      lenders: {
        title: "Prêteurs",
        points: [
          "Pipeline ouvert aux structures non conventionnelles : mezzanine, note vendeur, ABL, dette privée, LBO, prêteurs M&A",
          "Accent sur la valeur d'entreprise et les flux de trésorerie",
          "Packaging accéléré et aides à la décision",
          "Offres visibles sur les fiches, identité confidentielle jusqu'au LOI",
        ],
        cta: "Parcourir le pipeline",
      },
    },
    credibility: {
      items: [
        {
          h: "Intérêts alignés",
          p: "AcquizGroup vous connecte pour que nous ramions tous dans la même direction. SUCCÈS!",
        },
        {
          h: "Préparation d'abord",
          p: "Une préparation côté vendeur et des dossiers prêts pour les prêteurs réduisent les reprises et les risques de délais.",
        },
        {
          h: "Visibilité",
          p: "Des tableaux de bord et des artefacts standardisés apportent de la clarté à toutes les parties.",
        },
      ],
    },
    faq: {
      title: "Questions fréquentes",
      categories: [
        {
          category: "Général",
          target: "Tous les utilisateurs",
          items: [
            {
              q: "Qu'est-ce qu'AcquizGroup ?",
              a: "AcquizGroup est une plateforme de deal-flow qui connecte vendeurs, acheteurs, courtiers, consultants, partenaires et prêteurs dans un même écosystème pour accélérer les transmissions d'entreprise.",
            },
            {
              q: "En quoi AcquizGroup est-il différent des autres plateformes ?",
              a: "Nous offrons une standardisation de bout en bout : préparation côté vendeur, signaux de pré-financement et workflows unifiés qui réduisent les risques de délais et améliorent les taux de succès.",
            },
            {
              q: "Qui peut utiliser la plateforme ?",
              a: "Les vendeurs d'entreprises, acheteurs, courtiers, consultants, partenaires d'affaires et prêteurs. Chaque rôle dispose de fonctionnalités et workflows adaptés.",
            },
          ],
        },
        {
          category: "Programme Consultants (Rôle Opérationnel)",
          target: "Acheteurs et Vendeurs qui ont besoin de renforcer leur projet.",
          items: [
            {
              q: "Quelle est la différence entre un Consultant et un Expert Interne ?",
              a: "Le Consultant a un rôle opérationnel et stratégique. Il peut devenir votre partenaire d'affaires, fournir une licence spécifique ou gérer l'entreprise avec vous. L'Expert Interne se concentre uniquement sur la mécanique de la transaction (juridique, fiscal, notaire).",
            },
            {
              q: "Puis-je intégrer un consultant comme partenaire d'affaires (Équité) ?",
              a: "Absolument. AcquizGroup facilite la mise en relation avec des experts prêts à échanger leurs compétences ou licences contre une participation au capital, ce qui renforce votre dossier auprès des banques.",
            },
            {
              q: "Comment les consultants sont-ils vérifiés ?",
              a: "Chaque consultant fait l'objet d'une vérification des antécédents et d'une validation de ses compétences/licences avant d'être listé dans notre répertoire.",
            },
          ],
        },
        {
          category: "Programme Nos Experts (Rôle Transactionnel)",
          target: "Utilisateurs en phase de Due Diligence et Closing.",
          items: [
            {
              q: "Qui sont les experts internes d'AcquizGroup ?",
              a: "Ce sont des professionnels juridiques et financiers (Avocats d'affaires, Avocats en immigration, Notaires, CPA, Conseillers fiscaux) spécialisés exclusivement dans le processus de transmission d'entreprise.",
            },
            {
              q: "Suis-je obligé d'utiliser les experts de la plateforme ?",
              a: "Non. Vous êtes libre d'utiliser vos propres conseillers. Cependant, nos experts sont déjà formés aux workflows de la plateforme, ce qui accélère significativement les délais de closing et réduit les coûts de coordination.",
            },
            {
              q: "Comment les experts interviennent-ils dans mon dossier ?",
              a: "Ils sont accessibles directement depuis votre tableau de bord. Ils interviennent dans la révision des contrats, la structuration fiscale, l'audit financier (Due Diligence) et la signature finale.",
            },
          ],
        },
        {
          category: "Financement & Prêteurs",
          target: "Acheteurs et Prêteurs.",
          items: [
            {
              q: "Qu'est-ce que le \"Pré-financement\" sur une annonce ?",
              a: "Cela signifie que le dossier du vendeur a été analysé selon des métriques fondamentales (DSCR, flux de trésorerie) et qu'il a déjà reçu des manifestations d'intérêt ou des propositions de financement de nos prêteurs partenaires.",
            },
            {
              q: "Acceptez-vous le financement non conventionnel ?",
              a: "Oui. Notre réseau comprend des prêteurs spécialisés en dette mezzanine, financement vendeur, ABL (prêt basé sur les actifs) et capital privé, qui regardent le potentiel de l'entreprise au-delà du simple crédit personnel.",
            },
          ],
        },
        {
          category: "Confidentialité & Sécurité",
          target: "Général",
          items: [
            {
              q: "Qui peut voir mes informations confidentielles ?",
              a: "Personne, jusqu'à ce qu'un Accord de Non-Divulgation (NDA) soit signé numériquement sur la plateforme. Même après cela, vous contrôlez qui a accès à votre \"Data Room\".",
            },
            {
              q: "Comment AcquizGroup remplace-t-il mes outils actuels ?",
              a: "Tout est intégré : signature de documents (NDA/LOI), stockage sécurisé, vidéoconférence et suivi de progression. Vous n'avez plus besoin de jongler entre Zoom, Dropbox et vos chaînes d'emails.",
            },
          ],
        },
      ],
    },
    cta: {
      title: "Prêt à accélérer ?",
      subtitle: "Explorez nos forfaits adaptés à votre rôle.",
      btnSellers: "Forfaits Vendeurs",
      btnBuyers: "Forfaits Acheteurs",
      btnConsultants: "Forfaits Consultants",
    },
    partnersCta: {
      title: "Prêt à faire partie de l'Écosystème ?",
      btnPrimary: "Contacter l'équipe",
      btnSecondary: "Rejoindre l'Écosystème",
    },
    lendersCta: {
      title: "Prêt à financer l'avenir ?",
      btnPrimary: "Contacter l'équipe",
      btnSecondary: "Financer l'avenir",
    },
    footer: {
      legal: "© ",
    },
  },
} as const;
// ---------- END COPY ----------

const iconMap = {
  Shield: Shield,
  Target: Target,
  Rocket: Rocket,
  Unlock: Unlock,
};

const SectionCard: React.FC<{
  title: string;
  badge?: string;
  points: readonly string[];
  cta: string;
  href: string;
}> = ({ title, badge, points, cta, href }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="h-full flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition"
  >
    <div className="flex items-center gap-3 mb-3">
      <h3 className="text-lg font-semibold">{title}</h3>
      {badge && (
        <span className="text-xs rounded-full border px-2 py-0.5 bg-gray-50">{badge}</span>
      )}
    </div>
    <ul className="space-y-2 text-sm text-gray-700 list-disc pl-5 flex-1">
      {points.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </ul>
    <div className="mt-4 pt-2">
      <a
        href={href}
        className="inline-flex items-center justify-center rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
      >
        {cta}
      </a>
    </div>
  </motion.div>
);

export default function GeneralLanding() {
  const [lang, setLang] = useState<keyof typeof copy>("en");
  const t = useMemo(() => copy[lang], [lang]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 text-gray-900 font-sans">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b" style={{ backgroundColor: `${BRAND.navy}E6`, backdropFilter: 'blur(6px)' }}>
        <div className="mx-auto max-w-7xl px-4 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center header-logo-wrap">
            <img src="/noBgColor.png" alt="AcquizGroup" className="ag-logo-header" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-white/90">
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:opacity-80">
                Services <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
                <DropdownMenuItem asChild><Link to="/landing/sellers" className="cursor-pointer">{lang === 'fr' ? 'Pour les Vendeurs' : 'For Sellers'}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/buyers" className="cursor-pointer">{lang === 'fr' ? 'Pour les Acheteurs' : 'For Buyers'}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/consultants" className="cursor-pointer">{lang === 'fr' ? 'Pour les Consultants' : 'For Consultants'}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/brokers" className="cursor-pointer">{lang === 'fr' ? 'Pour les Courtiers' : 'For Brokers'}</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Ressources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:opacity-80">
                {lang === 'fr' ? 'Ressources' : 'Resources'} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
                <DropdownMenuItem asChild><Link to="/listings" className="cursor-pointer">{lang === 'fr' ? 'À vendre' : 'For Sale'}</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/blog" className="cursor-pointer">Blog</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/resources/e-learning" className="cursor-pointer">E-learning</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Our Partners Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:opacity-80">
                {lang === 'fr' ? 'Nos Partenaires' : 'Our Partners'} <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
                <DropdownMenuItem asChild><Link to="/landing/brokers" className="cursor-pointer">Brokers</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/lenders" className="cursor-pointer">Lenders</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/partners" className="cursor-pointer">Business Lawyers</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/partners" className="cursor-pointer">CPA</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/partners" className="cursor-pointer">Notary</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/landing/partners" className="cursor-pointer">Tax Advisor</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Portal Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 hover:opacity-80">
                Portal <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
                <DropdownMenuItem asChild><AuthLink portalKey="admin" className="cursor-pointer w-full">Admin Portal</AuthLink></DropdownMenuItem>
                <DropdownMenuItem asChild><AuthLink portalKey="business-partners" className="cursor-pointer w-full">Business Partners Portal</AuthLink></DropdownMenuItem>
                <DropdownMenuItem asChild><AuthLink portalKey="staff" className="cursor-pointer w-full">Staff Portal</AuthLink></DropdownMenuItem>
                <DropdownMenuItem asChild><AuthLink portalKey="dashboard" className="cursor-pointer w-full">My Dashboard</AuthLink></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "en" ? "fr" : "en")}
              className="rounded-2xl border px-3 py-1 text-xs font-medium text-white hover:bg-white/10"
              aria-label="Toggle language"
            >
              {lang === "en" ? "FR" : "EN"}
            </button>
            <Link to="/login" className="inline-flex items-center justify-center rounded-2xl border border-white/30 px-4 py-2 text-sm font-medium text-white hover:bg-white/10">
              {t.nav.login}
            </Link>
            <Link
              to="/about#contact"
              className="inline-flex items-center justify-center rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900"
              style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}
            >
              Contact
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <div className="rounded-3xl p-10 shadow-lg relative overflow-hidden" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
            <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
              <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
            </div>
            <div className="absolute inset-0 rounded-3xl" style={{ background: `linear-gradient(180deg, ${BRAND.navy}CC, ${BRAND.navyDark}CC)` }}></div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative text-sm tracking-wide uppercase" style={{ color: BRAND.silver }}>
            {t.hero.eyebrow}
          </motion.p>
          <motion.h1 initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative mt-2 text-4xl md:text-5xl font-extrabold text-white">
            {t.hero.title}
          </motion.h1>
          <motion.p initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="relative mt-4 max-w-3xl text-base md:text-lg" style={{ color: BRAND.silver }}>
            {t.hero.subtitle}
          </motion.p>
          <div className="relative mt-6 flex items-center gap-3">
            <a href="#how" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {t.hero.ctaSecondary}
            </a>
          </div>
          <div className="relative mt-6 h-1 w-24 rounded-full" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }} />
          </div>
        </div>
      </section>

      {/* Mission Section (NEW) */}
      <section className="mx-auto max-w-7xl px-4 pb-14">
        <div className="rounded-2xl border bg-white p-8 shadow-sm text-center">
          <h2 className="text-2xl md:text-3xl font-semibold mb-4" style={{ color: BRAND.navy }}>{t.mission.title}</h2>
          <p className="text-base md:text-lg max-w-4xl mx-auto" style={{ color: BRAND.graphite }}>
            {t.mission.text}
          </p>
        </div>
      </section>

      {/* Why Choose AcquizGroup - MOVED BEFORE How it works */}
      <section className="py-20" id="why-choose" style={{ backgroundColor: '#F7F9FC' }}>
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-center" style={{ color: BRAND.navy }}>
              {t.whyChoose.title}
            </h2>
            <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.graphite }}>
              {t.whyChoose.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.whyChoose.cards.map((card, i) => {
              const IconComponent = iconMap[card.icon as keyof typeof iconMap];
              return (
                <div key={i} className="rounded-2xl border bg-white p-6 shadow-sm">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: '#FFFFFF' }}>
                    <IconComponent className="h-5 w-5" style={{ color: BRAND.gold }} />
                  </div>
                  <h3 className="font-semibold mb-1" style={{ color: BRAND.navy }}>
                    {card.title}
                  </h3>
                  <p className="text-sm" style={{ color: BRAND.graphite }}>
                    {card.text}
                  </p>
                </div>
              );
            })}
          </div>

          {/* Built for every role - MOVED HERE */}
          <div className="mt-12">
            <h2 className="text-2xl md:text-3xl font-semibold mb-4" style={{ color: BRAND.navy }}>{t.grid.title}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div id="sellers">
                <SectionCard
                  title={t.personas.sellers.title}
                  points={t.personas.sellers.points}
                  cta={t.personas.sellers.cta}
                  href="/register/sellers"
                />
              </div>
              <div id="buyers">
                <SectionCard
                  title={t.personas.buyers.title}
                  points={t.personas.buyers.points}
                  cta={t.personas.buyers.cta}
                  href="/register/buyers"
                />
              </div>
              <div id="brokers">
                <SectionCard
                  title={t.personas.brokers.title}
                  badge={t.personas.brokers.badge}
                  points={t.personas.brokers.points}
                  cta={t.personas.brokers.cta}
                  href="/landing/brokers"
                />
              </div>
              <div id="consultants">
                <SectionCard
                  title={t.personas.consultants.title}
                  points={t.personas.consultants.points}
                  cta={t.personas.consultants.cta}
                  href="/landing/consultants"
                />
              </div>
              <div id="partners">
                <SectionCard
                  title={t.personas.partners.title}
                  points={t.personas.partners.points}
                  cta={t.personas.partners.cta}
                  href="/landing/partners"
                />
              </div>
              <div id="lenders">
                <SectionCard
                  title={t.personas.lenders.title}
                  points={t.personas.lenders.points}
                  cta={t.personas.lenders.cta}
                  href="/landing/lenders"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works - MOVED AFTER Why Choose */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-16">
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: BRAND.navy }}>{t.how.title}</h2>
          
          {/* Sellers Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3" style={{ color: BRAND.navy }}>{t.how.sellerLabel}</h3>
            <div className="flex flex-wrap gap-2">
              {t.how.sellerSteps.map((s, i) => (
                <div key={s.k} className="flex items-center">
                  <div className="rounded-xl border px-3 py-2 text-center text-sm" style={{ backgroundColor: "#F7F9FC", borderColor: "#E2E8F0" }}>
                    <span className="font-medium" style={{ color: BRAND.gold }}>{i + 1}.</span> {s.t}
                  </div>
                  {i < t.how.sellerSteps.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Buyers Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3" style={{ color: BRAND.navy }}>{t.how.buyerLabel}</h3>
            <div className="flex flex-wrap gap-2">
              {t.how.buyerSteps.map((s, i) => (
                <div key={s.k} className="flex items-center">
                  <div className="rounded-xl border px-3 py-2 text-center text-sm" style={{ backgroundColor: "#FDF8F0", borderColor: "#E8DCC8" }}>
                    <span className="font-medium" style={{ color: BRAND.gold }}>{i + 1}.</span> {s.t}
                  </div>
                  {i < t.how.buyerSteps.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Brokers Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3" style={{ color: BRAND.navy }}>{t.how.brokerLabel}</h3>
            <div className="flex flex-wrap gap-2">
              {t.how.brokerSteps.map((s, i) => (
                <div key={s.k} className="flex items-center">
                  <div className="rounded-xl border px-3 py-2 text-center text-sm" style={{ backgroundColor: "#F0F7FD", borderColor: "#C8D8E8" }}>
                    <span className="font-medium" style={{ color: BRAND.gold }}>{i + 1}.</span> {s.t}
                  </div>
                  {i < t.how.brokerSteps.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Consultants Steps */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3" style={{ color: BRAND.navy }}>{t.how.consultantLabel}</h3>
            <div className="flex flex-wrap gap-2">
              {t.how.consultantSteps.map((s, i) => (
                <div key={s.k} className="flex items-center">
                  <div className="rounded-xl border px-3 py-2 text-center text-sm" style={{ backgroundColor: "#F5F0FD", borderColor: "#D8C8E8" }}>
                    <span className="font-medium" style={{ color: BRAND.gold }}>{i + 1}.</span> {s.t}
                  </div>
                  {i < t.how.consultantSteps.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-base md:text-lg" style={{ color: BRAND.graphite }}>{t.how.blurb}</p>
        </div>
      </section>

      {/* Sellers CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="sellers-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Vendeurs' : 'Sellers'}</p>
          <h3 className="text-white font-semibold text-lg">
            {lang === 'fr' ? 'Prêt à vendre votre entreprise ?' : 'Ready to sell your business?'}
          </h3>
          <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.silver }}>
            {lang === 'fr' ? "Choisissez l'option qui vous convient : appel découverte ou démarrage de dossier en ligne." : "Choose the path that fits: discovery call or start your file online."}
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {lang === 'fr' ? "Contacter l'équipe" : 'Contact the team'}
            </Link>
            <Link to="/register/sellers" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {lang === 'fr' ? 'Démarrer mon dossier' : 'Start my file'}
            </Link>
          </div>
        </div>
      </section>

      {/* Buyers CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="buyers-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Acheteurs' : 'Buyers'}</p>
          <h3 className="text-white font-semibold text-lg">
            {lang === 'fr' ? 'Prêt à acheter une entreprise ?' : 'Ready to buy a business?'}
          </h3>
          <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.silver }}>
            {lang === 'fr' ? "Accédez à des dossiers structurés, des évaluations professionnelles et des options de financement adaptées." : "Access structured files, professional valuations, and tailored financing options."}
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {lang === 'fr' ? "Contacter l'équipe" : 'Contact the team'}
            </Link>
            <Link to="/register/buyers" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {lang === 'fr' ? 'Créer mon profil' : 'Create my profile'}
            </Link>
          </div>
        </div>
      </section>

      {/* Brokers CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="brokers-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Courtiers' : 'Brokers'}</p>
          <h3 className="text-white font-semibold text-lg">
            {lang === 'fr' ? 'Prêt à augmenter vos ventes ?' : 'Ready to increase your sales?'}
          </h3>
          <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.silver }}>
            {lang === 'fr' ? "Accélérez vos mandats avec le préfinancement et réactivez vos dossiers dormants." : "Accelerate your mandates with pre-financing and reactivate dormant files."}
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {lang === 'fr' ? "Contacter l'équipe" : 'Contact the team'}
            </Link>
            <Link to="/register/brokers" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {lang === 'fr' ? 'Soumettre mes mandats' : 'Submit my mandates'}
            </Link>
          </div>
        </div>
      </section>

      {/* Consultants CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="consultants-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Consultants' : 'Consultants'}</p>
          <h3 className="text-white font-semibold text-lg">
            {lang === 'fr' ? 'Prêt à monétiser votre expertise ?' : 'Ready to monetize your expertise?'}
          </h3>
          <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.silver }}>
            {lang === 'fr' ? "Élargissez votre clientèle, partagez vos connaissances et participez à des transactions réelles." : "Expand your client base, share your knowledge, and participate in real transactions."}
          </p>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {lang === 'fr' ? "Contacter l'équipe" : 'Contact the team'}
            </Link>
            <Link to="/register/consultant" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {lang === 'fr' ? "Rejoindre l'annuaire" : 'Join the directory'}
            </Link>
          </div>
        </div>
      </section>

      {/* Partners CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="partners-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Partenaires' : 'Partners'}</p>
          <h3 className="text-white font-semibold text-lg">
            {t.partnersCta.title}
          </h3>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {t.partnersCta.btnPrimary}
            </Link>
            <Link to="/register/partner" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {t.partnersCta.btnSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Lenders CTA */}
      <section className="mx-auto max-w-7xl px-4 py-8" id="lenders-highlight">
        <div className="rounded-2xl p-6 text-center" style={{ background: `linear-gradient(180deg, ${BRAND.navy}, ${BRAND.navyDark})` }}>
          <p className="text-xs uppercase tracking-wide mb-1" style={{ color: BRAND.gold }}>{lang === 'fr' ? 'Prêteurs' : 'Lenders'}</p>
          <h3 className="text-white font-semibold text-lg">
            {t.lendersCta.title}
          </h3>
          <div className="mt-4 grid md:grid-cols-2 gap-3">
            <Link to="/contact" className="rounded-2xl border px-4 py-2 text-sm font-semibold text-white border-white/30 hover:bg-white/10">
              {t.lendersCta.btnPrimary}
            </Link>
            <Link to="/register/lender" className="rounded-2xl px-4 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {t.lendersCta.btnSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Credibility - Animated with center scale and exit left */}
      <section className="mx-auto max-w-7xl px-4 py-16 overflow-hidden">
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <div className="grid md:grid-cols-3 gap-6">
            {t.credibility.items.map((item, i) => (
              <motion.div 
                key={i} 
                className="rounded-xl border bg-gray-50 p-4"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, x: -100 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ 
                  delay: i * 0.4, 
                  duration: 0.6,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
              >
                <h3 className="font-semibold" style={{ color: BRAND.navy }}>{item.h}</h3>
                <p className="text-base md:text-lg text-gray-700 mt-1">{item.p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="rounded-2xl border bg-white p-6 shadow-sm">
          <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: BRAND.navy }}>{t.faq.title}</h2>
          <div className="mt-6 space-y-8">
            {t.faq.categories.map((cat, catIdx) => (
              <div key={catIdx}>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: BRAND.navy }}>{cat.category}</h3>
                  <p className="text-sm" style={{ color: BRAND.graphite }}>{lang === 'fr' ? 'Cible' : 'Target'}: {cat.target}</p>
                </div>
                <div className="space-y-3">
                  {cat.items.map((f, i) => (
                    <details key={i} className="rounded-xl border p-4 bg-gray-50">
                      <summary className="font-medium cursor-pointer">{f.q}</summary>
                      <p className="mt-2 text-base md:text-lg text-gray-700">{f.a}</p>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24">
        <div className="rounded-2xl p-8 text-center shadow-lg" style={{ background: `linear-gradient(180deg, ${BRAND.navyDark}, ${BRAND.navy})` }}>
          <h2 className="text-2xl md:text-3xl font-semibold text-white">{t.cta.title}</h2>
          <p className="mt-2 text-base md:text-lg" style={{ color: BRAND.silver }}>{t.cta.subtitle}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/sellerplan" className="inline-flex items-center justify-center rounded-2xl px-5 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {t.cta.btnSellers}
            </Link>
            <Link to="/buyer/payment" className="inline-flex items-center justify-center rounded-2xl px-5 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {t.cta.btnBuyers}
            </Link>
            <Link to="/consultant/payment" className="inline-flex items-center justify-center rounded-2xl px-5 py-2 text-sm font-semibold text-gray-900" style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})` }}>
              {t.cta.btnConsultants}
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter lang={lang} />
      <ScrollToTop />
    </div>
  );
}
